<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useStore } from 'vuex';

import { useStoreGetters } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PreviewImage from 'dashboard/components/ui/PreviewImage.vue';
import { SUPPORTED_CHANNEL_TYPES } from 'dashboard/routes/dashboard/settings/sharedFiles/constants';

const store = useStore();
const { updateUISettings } = useUISettings();
const getters = useStoreGetters();

const currentChat = computed(() => store.getters.getSelectedChat);

const closePanel = () => {
  updateUISettings({
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
  });
};

const searchedKeyword = ref('');
const searchResults = ref([]);

const sharedFiles = computed(() => getters['sharedFiles/getSharedFiles'].value);
const listingFiles = computed(() => {
  return searchedKeyword.value ? searchResults.value : sharedFiles.value;
});

const channelType = computed(() => {
  return currentChat.value?.channel || currentChat.value?.meta?.channel || '';
});

const shouldShowSharedFilesPanel = computed(() => {
  return SUPPORTED_CHANNEL_TYPES.includes(channelType.value);
});

const searchFiles = event => {
  searchedKeyword.value = event.target.value.trim().toLowerCase();
  if (searchedKeyword.value) {
    searchResults.value = sharedFiles.value.filter(file =>
      file.name.toLowerCase().includes(searchedKeyword.value)
    );
  } else {
    searchResults.value = sharedFiles.value;
  }
};

const sendFile = fileId => {
  store.dispatch('sharedFiles/sendFile', {
    id: fileId,
    conversationId: currentChat.value.id,
  });
};

const fileIcon = type => {
  switch (type) {
    case 'image':
      return 'i-ri-image-line';
    case 'video':
      return 'i-ri-video-line';
    case 'document':
      return 'i-ri-file-text-line';
    default:
      return 'i-ri-file-line';
  }
};

onBeforeMount(() => {
  store.dispatch('sharedFiles/get');
});
</script>

<template>
  <div
    v-if="shouldShowSharedFilesPanel"
    class="ltr:border-l rtl:border-r border-n-weak h-full overflow-hidden z-10 w-[320px] min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px] flex flex-col bg-n-background"
  >
    <div class="flex flex-col h-full">
      <SidebarActionsHeader
        :title="$t('CONVERSATION.SIDEBAR.SHARED_FILES')"
        @close="closePanel"
      />
      <div
        v-if="sharedFiles.length"
        class="flex flex-1 overflow-hidden flex-col"
      >
        <input
          type="text"
          class="flex-none w-full p-2 border border-n-weak !outline-none !focus:outline-none !rounded-none !mb-0"
          :placeholder="$t('SHARED_FILE_MGMT.LIST.SEARCH')"
          @input="searchFiles"
        />
        <div class="flex-1 overflow-y-auto border-t border-n-weak">
          <ul>
            <li
              v-for="file in listingFiles"
              :key="file.id"
              class="file-item flex items-center py-3 px-4 pe-0 border-b border-n-weak hover:bg-n-solid-2 dark:hover:bg-n-solid-3 font-medium"
            >
              <a
                :href="file.file_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                <PreviewImage
                  v-if="file.file_type == 'image'"
                  :src="file.file_url"
                  :alt="file.name"
                  class="w-10 h-10 object-cover mr-3 rounded"
                />
                <div
                  v-else
                  class="flex items-center justify-center w-10 h-10 bg-n-solid-2 rounded mr-3"
                >
                  <Icon
                    :icon="fileIcon(file.file_type)"
                    class="text-4xl text-slate-600 dark:text-slate-400"
                  />
                </div>
              </a>
              <div class="flex flex-col">
                <span
                  class="text-sm font-medium text-slate-800 dark:text-slate-200"
                >
                  {{ file.name }}
                </span>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ `${file.file_size}KB - ${file.file_type}` }}
                </span>
              </div>
              <div
                class="file-action flex-1 flex justify-end opacity-0 transition-opacity duration-200"
              >
                <button
                  v-tooltip.left="$t('SHARED_FILE_MGMT.SIDEBAR.SEND')"
                  class="text-n-slate-11 dark:text-slate-100 hover:text-n-brand dark:hover:text-n-brand transition-colors duration-200 flex justify-center items-center"
                  @click.prevent="sendFile(file.id)"
                >
                  <Icon icon="i-ri-send-plane-fill" class="text-xl" />
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center h-full w-full"
      >
        <Icon
          icon="i-ri-file-unknow-line"
          class="text-5xl text-slate-400 dark:text-slate-600"
        />
        <p class="text-center text-sm p-4 text-slate-500 dark:text-slate-400">
          {{ $t('SHARED_FILE_MGMT.LIST.404') }}
        </p>
      </div>
    </div>
  </div>
  <template v-else />
</template>

<style lang="scss" scoped>
.file-item:hover .file-action {
  @apply opacity-100;
}
</style>
